import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTopHackers, getTopTaskPlayers } from '../services/api';
import './TopHacker.css';

const TopHacker = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('xp'); // 'xp' or 'tasks'
  const [topHackers, setTopHackers] = useState([]);
  const [topTaskPlayers, setTopTaskPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load leaderboard data based on active tab
  useEffect(() => {
    fetchLeaderboardData();
  }, [activeTab]);

  const fetchLeaderboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (activeTab === 'xp') {
        const response = await getTopHackers(25);
        setTopHackers(response.hackers || []);
      } else if (activeTab === 'tasks') {
        const response = await getTopTaskPlayers(25);
        setTopTaskPlayers(response.players || []);
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get user's rank in current leaderboard
  const getUserRank = () => {
    if (activeTab === 'xp') {
      const userIndex = topHackers.findIndex(hacker => hacker._id === user._id);
      return userIndex !== -1 ? userIndex + 1 : null;
    } else if (activeTab === 'tasks') {
      const userIndex = topTaskPlayers.findIndex(player => player._id === user._id);
      return userIndex !== -1 ? userIndex + 1 : null;
    }
    return null;
  };

  // Get current month/year for display
  const getCurrentMonth = () => {
    const date = new Date();
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    return activeTab === 'xp' ? topHackers : topTaskPlayers;
  };

  // Get column headers based on active tab
  const getColumnHeaders = () => {
    if (activeTab === 'xp') {
      return (
        <>
          <div className="rank">Rank</div>
          <div className="username">Hacker</div>
          <div className="xp">XP</div>
          <div className="status">Status</div>
        </>
      );
    } else {
      return (
        <>
          <div className="rank">Rank</div>
          <div className="username">Player</div>
          <div className="tasks">Tasks</div>
          <div className="xp">XP</div>
        </>
      );
    }
  };

  return (
    <div className="tophacker-container">
      <div className="tophacker-header">
        <h1 className="tophacker-title">🏆 Leaderboard</h1>
      </div>

      {/* Tab Navigation */}
      <div className="leaderboard-tabs">
        <button 
          className={`tab-button ${activeTab === 'xp' ? 'active' : ''}`}
          onClick={() => setActiveTab('xp')}
        >
          ⭐ Monthly XP
        </button>
        <button 
          className={`tab-button ${activeTab === 'tasks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tasks')}
        >
          🎯 Top 200 Tasks
        </button>
      </div>

      <div className="leaderboard-content">
        <div className="leaderboard-header">
          <h2>
            {activeTab === 'xp' 
              ? `Monthly XP Leaderboard - ${getCurrentMonth()}`
              : 'Top Task Players - All Time'
            }
          </h2>
          <p>
            {activeTab === 'xp' 
              ? 'Top 200 hackers ranked by XP this month'
              : 'Top 200 players ranked by community tasks completed'
            }
          </p>
          {user && user.role !== 'admin' && getUserRank() && (
            <div className="user-rank">
              Your Rank: #{getUserRank()} / {getCurrentData().length}
            </div>
          )}
          {user && user.role === 'admin' && (
            <div className="admin-notice">
              👑 Admins are not shown on the leaderboard
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading leaderboard...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div className="leaderboard-list">
            {getCurrentData().length === 0 ? (
              <p>
                {activeTab === 'xp' 
                  ? 'No hackers found. Start earning XP to appear on the leaderboard!'
                  : 'No task players found. Complete community tasks to appear on the leaderboard!'
                }
              </p>
            ) : (
              <div className="leaderboard-table">
                <div className="leaderboard-row header">
                  {getColumnHeaders()}
                </div>
                {getCurrentData().map((player, index) => (
                  <div 
                    key={player._id} 
                    className={`leaderboard-row ${player._id === user._id ? 'current-user' : ''} ${index < 3 ? `rank-${index + 1}` : ''}`}
                  >
                    <div className="rank">
                      {index === 0 && '🥇'}
                      {index === 1 && '🥈'}
                      {index === 2 && '🥉'}
                      {index > 2 && `#${index + 1}`}
                    </div>
                    <div className="username">
                      {player.username}
                      {player._id === user._id && ' (You)'}
                    </div>
                    {activeTab === 'xp' ? (
                      <>
                        <div className="xp">{player.xp.toLocaleString()}</div>
                        <div className="status">
                          {player.role === 'admin' ? '👑 Admin' : '🔹 Hacker'}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="tasks">{player.tasksCompleted}</div>
                        <div className="xp">{player.xp.toLocaleString()}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopHacker;